import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, ArrowRight, LogOut, CreditCard, Flame, Trophy, Users, Settings, LayoutDashboard, FileText, UtensilsCrossed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import GardenVisualization from '@/components/GardenVisualization';
import Navigation from '@/components/Navigation';
import TestimonialSubmitForm from '@/components/TestimonialSubmitForm';
import confetti from 'canvas-confetti';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [garden, setGarden] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [completedWeekNum, setCompletedWeekNum] = useState(null);
  const displayName = localStorage.getItem('display_name');

  // Confetti celebration function
  const triggerConfetti = useCallback(() => {
    const duration = 4000;
    const end = Date.now() + duration;

    const colors = ['#D4AF37', '#E8B4B8', '#4A7C59', '#FFD700', '#FF69B4'];

    (function frame() {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: colors
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());
  }, []);

  useEffect(() => {
    fetchDashboardData();
    
    // Check if we should show week completion confetti
    const showConfetti = localStorage.getItem('showWeekConfetti');
    const weekNum = localStorage.getItem('completedWeek');
    
    if (showConfetti === 'true') {
      setShowCelebration(true);
      setCompletedWeekNum(weekNum);
      localStorage.removeItem('showWeekConfetti');
      localStorage.removeItem('completedWeek');
      
      // Trigger confetti after a short delay
      setTimeout(() => {
        triggerConfetti();
      }, 500);
      
      // Hide celebration banner after 6 seconds
      setTimeout(() => {
        setShowCelebration(false);
      }, 6000);
    }
  }, [triggerConfetti]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [profileRes, gardenRes, progressRes] = await Promise.all([
        axios.get(`${API}/user/profile`, config),
        axios.get(`${API}/garden`, config).catch(() => null),
        axios.get(`${API}/user/progress`, config).catch(() => null)
      ]);

      setUser(profileRes.data);
      
      // Always update localStorage with the latest display_name from server
      if (profileRes.data.display_name) {
        localStorage.setItem('display_name', profileRes.data.display_name);
      }

      if (profileRes.data.subscription_active) {
        const weeksRes = await axios.get(`${API}/weeks`, config);
        setWeeks(weeksRes.data);
        if (gardenRes) {
          // If program is complete, ensure garden shows 100% and 84 days
          if (progressRes?.data?.program_complete) {
            setGarden({
              ...gardenRes.data,
              progress_percent: 100,
              total_days_completed: 84
            });
          } else {
            setGarden(gardenRes.data);
          }
        }
      }

      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.clear();
        navigate('/auth');
      } else {
        toast.error('Failed to load dashboard');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleSubscribe = () => {
    navigate('/pricing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-secondary-700 text-lg">Loading your journey...</p>
        </div>
      </div>
    );
  }

  if (!user?.subscription_active) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-secondary-50 to-accent-50 flex items-center justify-center px-6">
        <div className="max-w-2xl text-center">
          <Sparkles className="w-20 h-20 text-primary mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Welcome, {user?.display_name || displayName}!
          </h1>
          <p className="text-xl text-secondary-700 mb-8">
            Subscribe to unlock your 12-week transformation journey
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="subscribe-button"
              onClick={handleSubscribe}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg rounded-full shadow-lg"
            >
              <CreditCard className="mr-2 w-5 h-5" /> View Pricing
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="border-2 border-secondary-900 text-secondary-900 px-8 py-6 text-lg rounded-full"
            >
              <LogOut className="mr-2 w-5 h-5" /> Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      {/* Navigation */}
      <Navigation />
      
      {/* Week Completion Celebration Banner */}
      {showCelebration && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-gradient-to-r from-primary via-accent to-primary text-white py-4 px-6 text-center animate-pulse shadow-xl">
          <div className="flex items-center justify-center gap-3">
            <span className="text-3xl">🎉</span>
            <span className="text-xl font-bold">
              Congratulations! You completed Week {completedWeekNum}!
            </span>
            <span className="text-3xl">🎉</span>
          </div>
          <p className="text-sm mt-1 opacity-90">Your dedication is blooming beautifully!</p>
        </div>
      )}

      {/* Header */}
      <header className={`bg-white shadow-sm border-b border-primary/10 ${showCelebration ? 'mt-20' : ''}`}>
        <div className="container mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-secondary-900">
                Welcome Back, {user?.display_name || displayName}! ✨
              </h1>
              <p className="text-secondary-600 mt-1">Continue your radiant journey</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {user?.is_admin && (
                <Button
                  data-testid="admin-dashboard-button"
                  onClick={() => navigate('/admin/dashboard')}
                  size="sm"
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                >
                  <LayoutDashboard className="sm:mr-2 w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
                </Button>
              )}
              <Button
                data-testid="community-button"
                onClick={() => navigate('/community')}
                variant="outline"
                size="sm"
                className="text-primary border-primary hover:bg-primary/10"
              >
                <Users className="sm:mr-2 w-4 h-4" /> <span className="hidden sm:inline">Community</span>
              </Button>
              <Button
                data-testid="articles-button"
                onClick={() => navigate('/articles')}
                variant="outline"
                size="sm"
                className="text-primary border-primary hover:bg-primary/10"
              >
                <FileText className="sm:mr-2 w-4 h-4" /> <span className="hidden sm:inline">Articles</span>
              </Button>
              <Button
                data-testid="recipes-button"
                onClick={() => navigate('/recipes')}
                variant="outline"
                size="sm"
                className="text-primary border-primary hover:bg-primary/10"
              >
                <UtensilsCrossed className="sm:mr-2 w-4 h-4" /> <span className="hidden sm:inline">Recipes</span>
              </Button>
              <Button
                data-testid="settings-button"
                onClick={() => navigate('/account')}
                variant="ghost"
                size="sm"
                className="text-secondary-700 hover:text-primary"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-secondary-700 hover:text-primary"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        {/* Garden Section */}
        <section className="mb-12" data-testid="garden-section">
          <div className="bg-white rounded-3xl shadow-lg p-8 border border-primary/10">
            <div className="mb-6">
              <h2 className="text-3xl font-serif font-bold text-secondary-900 mb-2">
                Your Radiance Garden 🌸
              </h2>
              <p className="text-secondary-600">
                Day {garden?.current_day || 1} of Week {garden?.current_week || 1} • {garden?.total_days_completed || 0} of {garden?.total_days || 84} days completed
              </p>
            </div>
            <GardenVisualization garden={garden} userName={displayName} />
          </div>
        </section>

        {/* Testimonial Section - Show when user has completed 70+ days */}
        {garden?.total_days_completed >= 70 && (
          <section className="mb-12">
            <TestimonialSubmitForm />
          </section>
        )}

        {/* Streak Tracker Section */}
        <section className="mb-12" data-testid="streak-section">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Current Streak */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl shadow-lg p-8 border-2 border-orange-200">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-orange-400 to-amber-500 p-4 rounded-2xl shadow-lg">
                  <Flame className="w-10 h-10 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-orange-700 uppercase tracking-wide">Current Streak</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-orange-600" data-testid="current-streak-count">
                      {user?.current_streak || 0}
                    </span>
                    <span className="text-lg text-orange-600 font-medium">
                      {(user?.current_streak || 0) === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <p className="text-sm text-orange-600 mt-1">
                    {(user?.current_streak || 0) >= 7 
                      ? "🔥 You're on fire! Keep it going!" 
                      : (user?.current_streak || 0) >= 3 
                        ? "⚡ Great momentum! Don't break the chain!" 
                        : "Start your streak by completing today's task!"}
                  </p>
                </div>
              </div>
            </div>

            {/* Longest Streak */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-lg p-8 border-2 border-purple-200">
              <div className="flex items-center gap-4">
                <div className="bg-gradient-to-br from-purple-400 to-pink-500 p-4 rounded-2xl shadow-lg">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-purple-700 uppercase tracking-wide">Longest Streak</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-purple-600" data-testid="longest-streak-count">
                      {user?.longest_streak || 0}
                    </span>
                    <span className="text-lg text-purple-600 font-medium">
                      {(user?.longest_streak || 0) === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <p className="text-sm text-purple-600 mt-1">
                    {(user?.longest_streak || 0) >= 14 
                      ? "🏆 Amazing dedication! You're inspiring!" 
                      : (user?.longest_streak || 0) >= 7 
                        ? "🌟 A whole week! You're building a habit!" 
                        : "Your personal best - let's beat it!"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Current Day Highlight */}
        {garden && garden.current_week <= 12 && (
          <section className="mb-12" data-testid="current-day-section">
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl shadow-lg p-8 border-2 border-primary/20">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <span className="inline-block px-4 py-1 bg-primary text-white rounded-full text-sm font-semibold mb-4">
                    Week {garden.current_week}, Day {garden.current_day}
                  </span>
                  <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                    {weeks.find(w => w.week_number === garden.current_week)?.theme}
                  </h3>
                  <p className="text-secondary-700 mb-6">
                    Continue your journey - today's task is waiting for you!
                  </p>
                  <Button
                    data-testid="view-week-button"
                    onClick={() => navigate(`/week/${garden.current_week}`)}
                    className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full shadow-md"
                  >
                    View This Week's Tasks <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* All Weeks */}
        <section data-testid="all-weeks-section">
          <h2 className="text-3xl font-serif font-bold text-secondary-900 mb-8">Your 12-Week Journey</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {weeks.map((week) => (
              <div
                key={week.week_number}
                data-testid={`week-card-${week.week_number}`}
                onClick={() => navigate(`/week/${week.week_number}`)}
                className={`bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border-2 ${
                  week.week_number === garden?.current_week
                    ? 'border-primary'
                    : 'border-transparent hover:border-primary/30'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="px-3 py-1 bg-accent/10 text-accent-700 rounded-full text-sm font-semibold">
                    Week {week.week_number}
                  </span>
                  {week.week_number === garden?.current_week && (
                    <span className="px-3 py-1 bg-primary text-white rounded-full text-xs font-semibold">
                      Current
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-serif font-bold text-secondary-900 mb-2">{week.theme}</h3>
                <p className="text-secondary-600 text-sm mb-4 line-clamp-2">{week.description}</p>
                <div className="flex items-center text-primary text-sm font-medium">
                  View Details <ArrowRight className="ml-2 w-4 h-4" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default Dashboard;